import { useMemo } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import { MoreVertIcon } from "icons";

import ButtonDropdown from "components/ButtonDropdown";
import GroupLabel from "components/ButtonDropdown/GroupLabel";
import { MenuItem } from "components/Menu";
import Stack from "components/Stack";
import Text from "components/Text";

import { useLoggedUserIsDealAdmin } from "entities/Deal/hooks";
import { useDealChecklistPermission } from "entities/Deal/sdk";
import ShareWithButton from "entities/Package/components/ShareWithButton";
import StatusSelector from "entities/Package/components/StatusSelector";
import { IPackageCategory } from "entities/Package/sdk";
import DueDiligenceCategoryNewTaskButton from "entities/Task/components/DueDiligenceCategoryNewTaskButton";
import DueDiligenceCategoryOpenTaskButton from "entities/Task/components/DueDiligenceCategoryOpenTaskButton";
import { useTaskChannels } from "entities/Task/sdk";

interface IPackageCategoryMenuButton {
  category: IPackageCategory;
  packageId: number;
  dealId: number;
  updatePackage: ({ message }: { message?: string }) => void;
  handleCategoryMarkAllAsSeen: () => void;
  menuButtonDataTestid?: string;
  isShareablePage?: boolean;
}

const PackageCategoryMenuButton = ({
  category,
  packageId,
  dealId,
  isShareablePage,
  updatePackage,
  menuButtonDataTestid,
  handleCategoryMarkAllAsSeen,
}: IPackageCategoryMenuButton) => {
  const params = useParams<{
    dealId: string;
    packageId: string;
    tabId: string;
    sectionId: string;
    fieldId: string;
  }>();

  const tabId = Number(params.tabId);
  const { data: userPermission } = useDealChecklistPermission({
    dealId,
    packageId,
  });
  const { data: taskChannels } = useTaskChannels({ dealId });
  const { loggedUserIsDealAdmin } = useLoggedUserIsDealAdmin({ dealId });
  const tasksForCategory = useMemo(
    () =>
      taskChannels?.filter(
        (taskChannel) =>
          taskChannel.task.company_category?.id === category.category_id &&
          taskChannel.task.company_section === null &&
          taskChannel.task.company_field === null &&
          !taskChannel.task.completed
      ) || [],
    [category, taskChannels]
  );

  const hasFullAccess = useMemo(
    () => !_.isNil(userPermission) && userPermission.is_full_access,
    [userPermission]
  );

  const hasWriteAccess = useMemo(
    () => !_.isNil(userPermission) && userPermission.has_write_access,
    [userPermission]
  );

  const fieldIds = useMemo(
    () =>
      _.flatMap(category.sections, (section) =>
        section.fields.map((field) => field.field_id)
      ),
    [category]
  );

  return (
    <Stack flexDirection="row">
      {tasksForCategory.length > 0 && (
        <DueDiligenceCategoryOpenTaskButton
          taskChannels={tasksForCategory}
          category={category}
          dealId={dealId}
          tabId={tabId}
          packageId={packageId}
          updatePackage={updatePackage}
          taskButtonDataTestid="dd-open-task-button"
          taskMessagesButtonDataTestid="dd-open-new-task-messages-button"
        />
      )}
      <ButtonDropdown
        variant="text"
        size="small"
        label={<MoreVertIcon />}
        width="auto"
        sx={{ padding: "2px" }}
        keepMenuMounted
        buttonDataTestid={menuButtonDataTestid}
      >
        {!isShareablePage &&
          hasWriteAccess && [
            <GroupLabel label="status: mark all as" />,
            <StatusSelector
              fieldIds={fieldIds}
              companyPackageId={packageId}
              dealId={dealId}
              successCallback={updatePackage}
            />,
          ]}
        <GroupLabel label="Actions" />

        {loggedUserIsDealAdmin && (
          <ShareWithButton initiallySelectedItemIds={[category.category_id]} />
        )}

        {!isShareablePage && (
          <MenuItem
            onClick={handleCategoryMarkAllAsSeen}
            dataTestid="clear-updated-tags-option"
            key="mark-all-as-seen"
          >
            <Text variant="text2" data-testid="action-menu-option-name">
              Clear updated tags
            </Text>
          </MenuItem>
        )}

        {hasFullAccess && (
          <DueDiligenceCategoryNewTaskButton
            category={category}
            packageId={packageId}
            tabId={tabId}
            updatePackage={updatePackage}
            menuOptionDataTestid="create-new-task-option"
          />
        )}
      </ButtonDropdown>
    </Stack>
  );
};

export default PackageCategoryMenuButton;
